import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { User, Shield, Building2, Lock, Eye, EyeOff, Save, Loader2 } from 'lucide-react';

export default function ProfilePage() {
  const { user } = useAuth();
  const [showPwSection, setShowPwSection] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const roleLabel = { ADMIN: 'Administrador', COMPANY_ADMIN: 'Admin de Empresa', USER: 'Usuario' };

  const handleChangePw = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { toast.error('Las contraseñas no coinciden'); return; }
    if (newPw.length < 6) { toast.error('Minimo 6 caracteres'); return; }
    setSaving(true);
    try {
      await api.post('/api/auth/change-password', { current_password: currentPw, new_password: newPw });
      toast.success('Contraseña cambiada exitosamente');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setShowPwSection(false);
    } catch (err) { toast.error(err.message); }
    setSaving(false);
  };

  if (!user) return null;

  return (
    <div className="p-6 lg:p-8 max-w-2xl" data-testid="profile-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Mi Perfil</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Informacion de tu cuenta</p>
      </div>

      {/* User info - READ ONLY */}
      <div className="rounded-xl border p-6 mb-4" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
        <div className="flex items-center gap-4 mb-6">
          <div className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
            {user.first_name?.[0]}{user.last_name?.[0]}
          </div>
          <div>
            <h2 className="font-heading font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
              {user.first_name} {user.last_name}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--accent-primary)', color: '#fff' }}>
                <Shield size={9} /> {roleLabel[user.role] || user.role}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <ReadField label="Nombre" value={user.first_name} icon={User} />
          <ReadField label="Apellidos" value={user.last_name} icon={User} />
          <ReadField label="Email" value={user.email} icon={User} />
          <ReadField label="N. Empleado" value={user.employee_number} icon={User} />
          <ReadField label="Telefono" value={user.phone_number || 'No especificado'} icon={User} />
          <ReadField label="Empresa" value={user.company_name || 'Sin empresa'} icon={Building2} />
        </div>

        <p className="text-[10px] mt-4 italic" style={{ color: 'var(--text-muted)' }}>
          Estos campos son gestionados por tu administrador. Contactalo si necesitas cambios.
        </p>
      </div>

      {/* Password change */}
      <div className="rounded-xl border p-6" style={{ backgroundColor: 'var(--surface-primary)', borderColor: 'var(--border-color)' }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Lock size={16} style={{ color: 'var(--accent-primary)' }} />
            <h3 className="font-heading font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>Cambiar Contraseña</h3>
          </div>
          <button onClick={() => setShowPwSection(!showPwSection)} data-testid="toggle-password-change"
            className="text-xs font-medium" style={{ color: 'var(--accent-primary)' }}>
            {showPwSection ? 'Cancelar' : 'Cambiar'}
          </button>
        </div>

        {showPwSection && (
          <form onSubmit={handleChangePw} className="space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Contraseña actual</label>
              <input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} required data-testid="current-password"
                className="w-full px-3 py-2 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Nueva contraseña</label>
              <input type={showPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} required data-testid="new-password"
                className="w-full px-3 py-2 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Confirmar contraseña</label>
              <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required data-testid="confirm-password"
                className="w-full px-3 py-2 rounded-lg border text-sm outline-none" style={{ backgroundColor: 'var(--surface-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>
            <button type="submit" disabled={saving} data-testid="save-password-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Cambiar Contraseña
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ReadField({ label, value, icon: Icon }) {
  return (
    <div>
      <label className="block text-[10px] font-medium mb-0.5 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{label}</label>
      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{value || '-'}</p>
    </div>
  );
}
